import { Link } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   SeoArticlePage — Light Water redesign
   Frosted-white surfaces with aqua accents. Hero water-glass panel with
   gradient title, step cards on a brand-aqua hero side card, frosted
   section cards in main column, sidebar pill cloud, and a brand-aqua
   promo callout. All original named exports preserved.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

/* ─── Shared mixins ─────────────────────────────────────────────────── */

const waterGlass = css`
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
`;

const frostedPill = css`
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
`;

const primaryPill = css`
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow: var(--shadow-cta);
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Page shell ────────────────────────────────────────────────────── */

export const Page = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px 20px 80px;

  @media (max-width: 768px) {
    padding: 16px 12px 56px;
  }
`;

/* ─── Hero ──────────────────────────────────────────────────────────── */

export const Hero = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 28px;
  padding: 36px 36px 32px;
  margin-bottom: 28px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.32),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.22);
  isolation: isolate;
  overflow: hidden;
  animation: ${panelFloat} 6s ease-in-out infinite;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Aqua bloom on right edge */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 92% 0%,
      rgba(var(--color-brand-400-rgb), 0.28),
      transparent 55%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 28px 24px 26px;
  }

  @media (max-width: 600px) {
    padding: 24px 20px 22px;
    border-radius: 22px;
    gap: 22px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Eyebrow = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: var(--shadow-micro);
`;

export const Title = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: clamp(28px, 4.4vw, 44px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: normal;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const Intro = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
  max-width: 60ch;
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

export const MetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  ${frostedPill};
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* ─── Hero side card (steps + try CTA) ─────────────────────────────── */

export const HeroCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 22px;
  border-radius: 24px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 60%,
    var(--color-brand-700) 100%
  );
  color: var(--glass-text-on-pill);
  box-shadow:
    0 26px 50px -20px rgba(var(--color-brand-800-rgb), 0.45),
    0 10px 22px rgba(var(--color-brand-800-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.5),
    inset 0 -8px 18px rgba(0, 0, 0, 0.16);
  overflow: hidden;

  /* Top sheen on the colored card */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 22px;
    right: 22px;
    height: 14px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const CardLabel = styled.p`
  margin: 0 0 6px;
  color: rgba(var(--glass-white-rgb), 0.85);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
`;

export const CardText = styled.p`
  margin: 0;
  color: rgba(var(--glass-white-rgb), 0.92);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
`;

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.32);
  background: rgba(var(--glass-white-rgb), 0.16);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const StepNumber = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: var(--glass-base-1);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 1);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const StepCopy = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StepHeading = styled.h3`
  margin: 0;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
`;

export const StepBody = styled.p`
  margin: 0;
  color: rgba(var(--glass-white-rgb), 0.85);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`;

export const KeywordList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  li {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(var(--glass-white-rgb), 0.4);
    background: rgba(var(--glass-white-rgb), 0.18);
    color: var(--glass-text-on-pill);
    font-family: var(--font-family-primary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: -0.005em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Two-column body ──────────────────────────────────────────────── */

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 22px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SectionCard = styled.section`
  ${waterGlass};
  border-radius: 22px;
  padding: 26px 28px;
  transition:
    border-color 220ms var(--ease-snappy),
    transform 220ms var(--ease-snappy);

  &:hover {
    border-color: var(--color-line-strong);
    transform: translateY(-1px);
  }

  @media (max-width: 600px) {
    padding: 22px 20px;
    border-radius: 18px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-line);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

export const Paragraph = styled.p`
  margin: 0 0 12px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 400;
  line-height: normal;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

export const BulletList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    position: relative;
    padding-left: 22px;
    color: var(--color-text-body);
    font-family: var(--font-family-primary);
    font-size: 14.5px;
    line-height: normal;

    &::before {
      content: "";
      position: absolute;
      left: 4px;
      top: 9px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: linear-gradient(
        180deg,
        var(--color-brand-300) 0%,
        var(--color-brand-500) 100%
      );
      box-shadow:
        0 2px 6px rgba(var(--color-brand-500-rgb), 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.55);
    }

    strong {
      color: var(--color-text-strong);
      font-weight: 700;
    }
  }
`;

/* ─── FAQ ──────────────────────────────────────────────────────────── */

export const FaqSection = styled.section`
  ${waterGlass};
  border-radius: 22px;
  padding: 26px 28px;

  @media (max-width: 600px) {
    padding: 22px 20px;
    border-radius: 18px;
  }
`;

export const FaqItem = styled.article`
  padding: 14px 0;
  border-bottom: 1px solid var(--color-line);

  &:last-child {
    border-bottom: none;
  }
`;

export const Question = styled.h3`
  margin: 0 0 6px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: normal;
`;

export const Answer = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
`;

/* ─── Sidebar link cloud ───────────────────────────────────────────── */

export const LinkCloud = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

export const LinkPill = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  ${frostedPill};
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-decoration: none;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
    color: var(--color-brand-700);
  }
`;

/* ─── Action buttons ───────────────────────────────────────────────── */

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`;

const actionPillBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);
`;

export const PrimaryAction = styled(Link)`
  ${actionPillBase};
  ${primaryPill};
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4.5s infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

export const PrimaryActionButton = styled.button`
  ${actionPillBase};
  ${primaryPill};
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4.5s infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

export const SecondaryAction = styled(Link)`
  ${actionPillBase};
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }
`;

/* ─── Article grid (kept for downstream usage) ─────────────────────── */

export const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

export const ArticleCard = styled.article`
  ${waterGlass};
  border-radius: 22px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy);

  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-line-strong);
  }
`;

export const ArticleTitleLink = styled(Link)`
  color: var(--color-text-strong);
  text-decoration: none;
  font-family: var(--font-family-primary);
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.015em;
  line-height: normal;
  transition: color 180ms var(--ease-snappy);

  &:hover {
    color: var(--color-brand-700);
  }
`;

export const ArticleExcerpt = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
`;

export const ArticleMeta = styled.p`
  margin: 0;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

/* ─── Promo callout (mid-article CTA) ──────────────────────────────── */

export const PromoSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px 30px;
  border-radius: 24px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 60%,
    var(--color-brand-700) 100%
  );
  color: var(--glass-text-on-pill);
  box-shadow:
    0 28px 52px -20px rgba(var(--color-brand-800-rgb), 0.46),
    0 10px 22px rgba(var(--color-brand-800-rgb), 0.24),
    inset 0 2px 0 rgba(255, 255, 255, 0.5),
    inset 0 -8px 18px rgba(0, 0, 0, 0.16);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 28px;
    right: 28px;
    height: 14px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  /* On the colored callout, swap secondary buttons to white frost so they
     contrast against the aqua background. */
  ${ActionRow} ${SecondaryAction} {
    background: var(--glass-base-1);
    border-color: var(--glass-edge);
    color: var(--color-brand-700);

    &:hover {
      background: var(--glass-base-1);
      border-color: var(--glass-edge-strong);
    }
  }

  @media (max-width: 600px) {
    padding: 24px 22px;
    border-radius: 20px;
  }
`;

export const PromoCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 640px;
`;

export const PromoTitle = styled.h2`
  margin: 0;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: normal;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
`;

export const PromoText = styled.p`
  margin: 0;
  color: rgba(var(--glass-white-rgb), 0.92);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 500;
  line-height: normal;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
`;
